/**
 * `labelForClient` — pick the pair of strings the AccountSwitcher
 * renders for a linked client (ADR-0237 Slice 1 UX fix, 2026-10-11).
 *
 * The product problem: `defaultAccountName(email)` on the server
 * derives an account's initial display name from the owner's email
 * domain root (e.g. `rich+lersirg@vbout.com` → `"Vbout"`). When the
 * agency owner and the client owner share an email domain, both
 * accounts end up with the SAME display name. The switcher shows
 * every row as "Vbout" and the agency user can no longer tell which
 * is which.
 *
 * The fix: when the client's name collides with the agency's own
 * name (or is empty / whitespace) AND a primary domain is known,
 * promote the primary domain to the primary label — the domain is
 * the discriminating identity in that case. Otherwise the account
 * name stays primary and the domain is the subtitle (unchanged
 * behavior for every other client).
 *
 * Pure. Case-insensitive. No DOM, no network — safe for tests.
 */

export interface ClientLabel {
  /** Line 1 — the discriminating label (name OR domain). */
  readonly primary: string;
  /** Line 2 — the subtitle, or `null` to render no second line. */
  readonly secondary: string | null;
}

/**
 * Decide whether the client's own `name` is informative in the
 * context of the owning agency's name, or whether the primary
 * domain should be promoted to the main label. Exported for tests.
 */
export function shouldPreferDomainLabel(
  client_name: string,
  agency_name: string,
  primary_domain: string | null,
): boolean {
  if (primary_domain === null) return false;
  const nn = client_name.trim();
  const aa = agency_name.trim();
  if (nn.length === 0) return true;
  if (aa.length > 0 && nn.toLowerCase() === aa.toLowerCase()) return true;
  return false;
}

export function labelForClient(
  client: { readonly client_name: string; readonly primary_domain: string | null },
  agency_name: string,
): ClientLabel {
  if (shouldPreferDomainLabel(client.client_name, agency_name, client.primary_domain)) {
    // `primary_domain` is non-null when the predicate returns true.
    return { primary: client.primary_domain as string, secondary: null };
  }
  return {
    primary: client.client_name.trim(),
    secondary: client.primary_domain,
  };
}
